import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/AuthShell";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { acceptSession } = useAuth();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      acceptSession(data);
      toast.success("Password updated — you're signed in");
      nav("/app");
    } catch (err) {
      toast.error(errText(err, "Couldn't reset the password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell label="Account recovery" sub="Pick something you'll remember. You'll be signed in right after."
      heading={<>A fresh<br />password,<br /><span className="italic text-primary">one minute.</span></>}>
      <div>
        <h2 className="font-serif-display text-3xl mb-2">Choose a new password</h2>
        <p className="text-sm text-muted-foreground">Changed your mind? <Link to="/login" className="underline">Log in</Link></p>
      </div>
      {!token ? (
        <div data-testid="reset-missing-token" className="border border-foreground p-5 space-y-2">
          <p className="font-medium">This link is incomplete</p>
          <p className="text-sm text-muted-foreground">Open the link from your email again, or <Link to="/forgot-password" className="underline">request a new one</Link>.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="password" className="font-mono-label">New password</Label>
              <Input id="password" data-testid="reset-password" type="password" required autoFocus minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="confirm" className="font-mono-label">Repeat password</Label>
              <Input id="confirm" data-testid="reset-confirm" type="password" required minLength={6} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} className="rounded-sm mt-1.5 h-11" />
            </div>
          </div>
          <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full h-11 bg-foreground text-background hover:bg-primary rounded-sm btn-invert">
            {loading ? "Saving…" : "Save new password →"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
